import axios, { AxiosResponse } from 'axios';

export const defaultCustomerPolicy: CustomerPolicy = {
	coverageAmount: undefined,
	rate: undefined,
	term: undefined,
	statusId: '',
	effectiveDate: '',
	stripePlanId: '',
};

export const defaultCustomerPolicyBeneficiary: CustomerPolicyBeneficiaryDto = {
	customerPolicyId: '',
	disbursementPercent: undefined,
	statusId: '',
	relationship: '',
	isRevocable: true,
	typeId: 'ea0bdce7b30e41f48ffbdce43d437497',
	shouldSendNotification: false,
};

export const defaultTrustee: CustomerPolicyBeneficiaryDto = {
	customerPolicyId: '',
	disbursementPercent: undefined,
	firstName: '',
	lastName: '',
	email: '',
	statusId: '',
	typeId: 'ec0bdce7b30e41f48ffbdce43d437497'
};

export const defaultPlanOptions: CustomerPolicy[] = [{}];

export interface StripeDeleted {
	id: string;
	deleted: boolean;
}

export const defaultDeletedPlan: StripeDeleted = {
	id: '',
	deleted: false
};

export const devQuestionnaire = {
	heightUnit: 'in',
	weightUnit: 'lbs',
	maxWeightLoss: 8,
	ownPetsTypes: {},
	foreignTravelList: [],
	cancer: {
		cancerNone: true,
		isValid: true
	},
	heartCirculatory: {
		heartCirculatoryNone: true,
		isValid: true
	},
	psychological: {
		psychologicalNone: true,
		isValid: true
	},
	reproductive: {
		reproductiveNone: true,
		isValid: true
	},
	head: {
		headNone: true,
		isValid: true
	},
	immuno: {
		immunoNone: true,
		isValid: true
	},
	blood: {
		bloodNone: true,
		isValid: true
	},
	liver: {
		liverNone: true,
		isValid: true
	},
	respiratory: {
		respiratoryNone: true,
		isValid: true
	},
	neurological: {
		neurologicalNone: true,
		isValid: true
	},
	declareTrue: true,
	isCanadian: true,
	hasBeenCancelled: false,
	pendingCoverage: false,
	heightft: '6',
	height: '0',
	weight: '193',
	lostWeight: false,
	occupation: 'Employed',
	specificJob: 'occupationNoneOfTheAbove',
	jobDescription: 'Other',
	annualIncome: '55,555',
	householdIncome: '55,555',
	exerciseFrequentlyHours: '1',
	exerciseFrequentlyMinutes: '0',
	ownPets: false,
	driversLicense: false,
	speedingViolations: false,
	impairedViolations: false,
	otherViolations: false,
	revokedLicense: false,
	criminalOffense: false,
	activities: {
		activitiesNoneOfTheAbove: true
	},
	abroad: false,
	consumedAlcohol: false,
	usedTobacco: false,
	cannabis: false,
	controlledSubstances: false,
	substanceCounseling: false,
	furtherExamination: false,
	notConsultedDoctor: false,
	prescriptionPainkillers: false,
	familyDiagnosis: false,
	familyKidneyDisease: false
};

export interface ForeignTravel {
	abroadLocation: string;
	abroadIsraelPalestine?: boolean;
	abroadDuration: string;
}
export const defaultForeignTravel: ForeignTravel = {
	abroadLocation: '',
	abroadDuration: ''
};

export const defaultDeclineChecker: DeclineCheckerDto = {
	isDeclined: false
};

const mockAdvisoryOptions: CustomerPolicy[] = [
	{
		rate: 23,
		coverageAmount: 350000,
		term: 15
	},
	{},
	{}
];

const mockBespokeOptions: CustomerPolicy[] = [
	{
		rate: 10,
		term: 15
	},
	{
		rate: 15,
		term: 15
	},
	{
		rate: 23,
		term: 15
	}
];

const mockSimpleOptions: CustomerPolicy[] = [
	{
		rate: 10,
		coverageAmount: 220000,
		term: 15
	},
	{
		rate: 15,
		coverageAmount: 220000,
		term: 15
	},
	{
		rate: 23,
		coverageAmount: 350000,
		term: 15
	}
];

const apiUrl = process.env.REACT_APP_API_URL;
const policyAPI = {
	fetchPlanOptions: (workflow?: string) => {
		return new Promise((resolve, reject) => {
			setTimeout(
				() => {
					switch (workflow) {
						case 'advisory':
							resolve(mockAdvisoryOptions);
							break;
						case 'bespoke':
							resolve(mockBespokeOptions);
							break;
						case 'simple':
							resolve(mockSimpleOptions);
							break;
						default:
							resolve(mockSimpleOptions);
							break;
					}
				},
				1000);
		});
	},
	fetchSimplePlanOptions: (params: GetSimplePoliciesRequest) => {
		const data = { params };
		return axios.get(`${apiUrl}/rates-simple.json`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchAdvisoryPlanOptions: (params: GetAdvisoryPoliciesRequest) => {
		const data = { params };
		return axios.get(`${apiUrl}/rates-advisory.json`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchBespokePlanOptions: (params: GetBespokePoliciesRequest) => {
		const data = { params };
		return axios.get(`${apiUrl}/rates-bespoke.json`, data)
			.then((resp: AxiosResponse) => resp.data);
	},

	fetchCustomerPolicy: (userId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/policies/${policyId}.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomerPolicyFreeTrialMonths: (userId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/policies/${policyId}/free-trial-month.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomerPolicyDocumentUrl: (userId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/policies/${policyId}/pdf`)
			.then((resp: AxiosResponse) => resp.data as string);
	},
	fetchCustomerPolicies: (userId: string, includeInactive?: boolean) => {
		const data = {
			params: { includeInactive: includeInactive || false }
		};

		return axios.get(`${apiUrl}/customers/${userId}/policies.json`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	sendBeneficiaryUploadedConfirmation: (userId: string, policyId: string) => {
		return axios.post(`${apiUrl}/emails/beneficiary-uploaded-confirmation`, { userId, policyId })
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchRatePlusMinus: (policyId: string) => {
		return axios.get(`${apiUrl}/rate-plus-minus/${policyId}.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchIsHardDeclined: (userId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/policies/${policyId}/isHardDeclined.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchIsSoftDeclined: (userId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/policies/${policyId}/isSoftDeclined.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchQuestionnaireReport: (userId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/policies/${policyId}/questionnaire-report.csv`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchPolicyAnswer: (policyId: string) => {
		return axios.get(`${apiUrl}/policies/${policyId}/questionnaire-answers.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	updatePolicyPaperCopyStatus: (policyId: string) => {
		return axios.post(`${apiUrl}/policies/${policyId}/mailed-copy.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveCustomerPolicy: (customerPolicy: CustomerPolicy) => {
		return axios.post(`${apiUrl}/customers/policy`, customerPolicy)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveCustomerPolicyAnswer: (policyId: string, questionName: string, value: string) => {
		const data = { policyId, questionName, value };
		return axios.post(`${apiUrl}/customers/policy/answer`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	updatePolicy: (policyId: string) => {
		const data = { policyId };
		return axios.post(`${apiUrl}/update-policy`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	deleteCustomerPolicy: (stripeCustomerId?: string, stripePlanId?: string) => {
		const data = { stripeCustomerId, stripePlanId };
		return axios.post(`${apiUrl}/customers/policy/delete`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	rebuildCustomerPolicyPdf: (customerId: number, policyId: string) => {
		return axios.post(`${apiUrl}/customers/${customerId}/policies/${policyId}/update-pdf.json`)
			.then(resp => resp.data);
	},
	saveAdminCustomerPolicy: (customerPolicy: AdminUpdateCustomerPolicyRequest) => {
		return axios.post(`${apiUrl}/admin/customers/policy`, customerPolicy)
			.then((resp: AxiosResponse) => resp.data);
	},

	fetchCustomerPolicyBeneficiary: (userId: string, beneficiaryId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/beneficiaries/${beneficiaryId}.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomerPolicyBeneficiaries: (userId: string) => {
		return axios.get(`${apiUrl}/customers/${userId}/beneficiaries.json`)
			.then((resp: AxiosResponse) => resp.data);
	},
	fetchCustomerPolicyBeneficiaryForm: (userId: string, policyId: string, formId: string) => {
		// TODO: the process of updating policy status probably should be separated into another method
		return new Promise((resolve) => {
			window.location.href = `${apiUrl}/customers/${userId}/policies/${policyId}/beneficiaryform/${formId}`;
			setTimeout(() => resolve(true), 3000);
		});
	},
	saveCustomerPolicyBeneficiary: (customerPolicyBeneficiary: CustomerPolicyBeneficiaryDto) => {
		return axios.post(`${apiUrl}/customers/beneficiary`, customerPolicyBeneficiary)
			.then((resp: AxiosResponse) => resp.data);
	},
	saveCustomerPolicyCurrentBeneficiary: (customerPolicyCurrentBeneficiary: CustomerPolicyBeneficiaryDto) => {
		return axios.post(`${apiUrl}/customers/confirmed-beneficiary`, customerPolicyCurrentBeneficiary)
			.then((resp: AxiosResponse) => resp.data);
	},
	approveAllBeneficiariesForCustomer: (request: ApproveAllBeneficiariesForNewCustomerRequest) => {
		return axios.post(`${apiUrl}/customers/${request.id}/policies/${request.policyId}/approve-all-beneficiaries`, request)
			.then((resp: AxiosResponse) => resp.data);
	},
	deleteCustomerPolicyBeneficiary: (beneficiaryId: string, statusId: string) => {
		const data = { beneficiaryId, statusId };
		return axios.post(`${apiUrl}/customers/beneficiary/delete`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	deleteBeneficiaryDuringSignUp: (beneficiaryId: string) => {
		const data = { beneficiaryId };
		return axios.post(`${apiUrl}/beneficiaries/delete`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
	sendBeneficiaryNotification: (userId: string, beneficiaryId: string) => {
		const data = { userId, beneficiaryId };
		return axios.post(`${apiUrl}/emails/beneficiary-notification`, data)
			.then(resp => resp.data);
	},
	uploadSignedBeneficiaryDocumentUrl: (customerId: string, policyId: string, file: any) => {		
		const url = `${apiUrl}/admin/customers/${customerId}/policies/${policyId}/beneficiaries/upload-signed-form`;
		let data = new FormData();
		data.append('file', file);
		
		return axios.post(url, data)
		.then((resp: AxiosResponse) => resp.data);
	},
	fetchSignedBeneficiaryDocumentUrl: (customerId: string, policyId: string) => {
		return axios.get(`${apiUrl}/customers/${customerId}/policies/${policyId}/beneficiaries/get-signed-form-url`)
			.then((resp: AxiosResponse) => resp.data as string);			
	},
	sendCancelPolicyEmail: (userId: string, policyId: string) => {
		const data = {
			userId,
			policyId,
		};
		return axios.post(`${apiUrl}/emails/cancelpolicy-notification`, data)
			.then((resp: AxiosResponse) => resp.data);
	},
};

export default policyAPI;


// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/policy/policyAPI.ts